import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import {
  MdAdd,
  MdBadge,
  MdCheckCircle,
  MdContacts,
  MdLocalHospital,
  MdMedicalServices,
  MdPeople,
  MdPerson,
  MdPhone,
  MdEdit,
  MdDelete,
  MdClose,
  MdSearch,
  MdDirectionsRun,
} from "react-icons/md";
import { tokenManager } from "utils/tokenManager";

const API_BASE = "https://usibras-web-backend.onrender.com/api/shift-sms/recipients";

const normalizeRecipient = (r) => ({
  id: r.id,
  staffId: r.staff_id,
  fullName: r.full_name,
  contact: r.contact,
  role: r.role,
  type: r.type ?? null,
  roleType: r.role_type ?? null,
  department: r.department ?? null,
});

const roleBadge = (role) =>
  role === "Manager"
    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
    : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";

const roleTypeBadge = (roleType) =>
  roleType === "Supervisor"
    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
    : "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300";

const getDateString = (date) => date.toISOString().slice(0, 10);

const HRManager = () => {
  const [recipients, setRecipients] = useState([]);
  const [passRecords, setPassRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedPersonId, setSelectedPersonId] = useState("");

  const today = new Date();
  const minDate = getDateString(today);
  const maxDate = getDateString(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30));

  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(minDate);
  const [passReason, setPassReason] = useState("Official Pass");
  const [notes, setNotes] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const getHeaders = () => {
    const auth = tokenManager.getAuthHeader();
    return { "Content-Type": "application/json", ...(auth ? { Authorization: auth } : {}) };
  };

  const API_PASSES = "https://usibras-web-backend.onrender.com/api/hr-passes";

  const fetchPasses = async (query = "") => {
    try {
      const endpoint = `${API_PASSES}/hr${query ? `?${query}` : "?status=Granted"}`;
      const res = await fetch(endpoint, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load passes");
      // map API shape to UI shape
      const items = (data.data || data || []).map((r) => ({
        id: r.id,
        personId: r.person_id ?? r.personId,
        staffId: r.staff_id ?? r.staffId,
        fullName: r.full_name ?? r.fullName,
        role: r.role,
        roleType: r.role_type ?? r.roleType,
        department: r.department,
        startDate: r.start_date ?? r.startDate,
        endDate: r.end_date ?? r.endDate,
        reason: r.reason,
        notes: r.notes,
        status: r.status,
      }));
      setPassRecords(items);
    } catch (err) {
      setFormMessage(err.message || "Could not load passes");
    }
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load recipients");
      setRecipients((data.data || []).map(normalizeRecipient));
    } catch (error) {
      setRecipients([]);
      setFormMessage(error.message || "Unable to load SMS recipients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipients();
    fetchPasses();
  }, []);

  const filteredRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((person) => {
      return [person.fullName, person.staffId, person.contact, person.role, person.roleType, person.department]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [recipients, search]);

  const analysisCards = useMemo(() => {
    const staffCount = recipients.filter((person) => person.role === "Staff").length;
    const supervisorCount = recipients.filter((person) => person.roleType === "Supervisor").length;
    const activePassCount = passRecords.filter((p) => p.status === "Granted").length;
    return [
      { label: "Total recipients", value: recipients.length, icon: MdPeople, color: "text-blue-500 bg-blue-100" },
      { label: "Staff", value: staffCount, icon: MdPerson, color: "text-green-500 bg-green-100" },
      { label: "Supervisors", value: supervisorCount, icon: MdMedicalServices, color: "text-indigo-500 bg-indigo-100" },
      { label: "Active passes", value: activePassCount, icon: MdDirectionsRun, color: "text-amber-500 bg-amber-100" },
    ];
  }, [passRecords, recipients]);

  const clampDate = (value) => {
    if (!value) return minDate;
    if (value < minDate) return minDate;
    if (value > maxDate) return maxDate;
    return value;
  };

  const handleStartDateChange = (value) => {
    const next = clampDate(value);
    if (next > endDate) setEndDate(next);
    setStartDate(next);
  };

  const handleEndDateChange = (value) => {
    const next = clampDate(value);
    if (next < startDate) setStartDate(next);
    setEndDate(next);
  };

  const handleGrantPass = (e) => {
    e.preventDefault();
    if (!selectedPersonId) {
      setFormMessage("Please select a person before granting a pass.");
      return;
    }
    const person = recipients.find((p) => String(p.id) === String(selectedPersonId));
    if (!person) {
      setFormMessage("Selected recipient could not be found.");
      return;
    }
    // POST to API
    (async () => {
      try {
        const body = {
          full_name: person.fullName,
          staff_id: person.staffId,
          role: person.role,
          role_type: person.roleType,
          department: person.department,
          start_date: startDate,
          end_date: endDate,
          reason: passReason,
          notes: notes.trim(),
        };
        const res = await fetch(API_PASSES, { method: "POST", headers: getHeaders(), body: JSON.stringify(body) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to create pass");
        await fetchPasses();
        setSelectedPersonId("");
        setStartDate(minDate);
        setEndDate(minDate);
        setPassReason("Official Pass");
        setNotes("");
        setFormMessage(`${person.fullName} granted pass ${startDate} → ${endDate}`);
      } catch (err) {
        setFormMessage(err.message || "Could not create pass");
      }
    })();
  };

  const handleUpdatePass = async (id, updates = {}) => {
    try {
      const res = await fetch(`${API_PASSES}/${id}`, { method: "PUT", headers: getHeaders(), body: JSON.stringify(updates) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Update failed");
      await fetchPasses();
      setFormMessage("Pass updated");
    } catch (err) {
      setFormMessage(err.message || "Could not update pass");
    }
  };

  const handleDeletePass = async (id) => {
    if (!window.confirm("Delete this pass record?")) return;
    try {
      const res = await fetch(`${API_PASSES}/${id}`, { method: "DELETE", headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed");
      await fetchPasses();
      setFormMessage("Pass deleted");
    } catch (err) {
      setFormMessage(err.message || "Could not delete pass");
    }
  };

  return (
    <div className="mt-5 h-full space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <MdDirectionsRun className="h-8 w-8 text-amber-500" />
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white">HR Manager</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage recipients and grant passes to staff.</p>
        </div>
        <div className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600 dark:bg-amber-900/20 dark:text-amber-300">
          {recipients.length} active recipients
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {analysisCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} extra="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">{value}</p>
              </div>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.4fr_0.8fr]">
        <Card extra="p-0 overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-navy-700">
            <div className="flex items-center gap-2">
              <MdContacts className="h-5 w-5 text-brand-500" />
              <h3 className="text-base font-semibold text-navy-700 dark:text-white">SMS Users</h3>
            </div>
            <div className="relative w-full max-w-xs">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or staff ID"
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
              <thead className="bg-gray-50 dark:bg-navy-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Staff ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Pass Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">Loading recipients…</td>
                  </tr>
                ) : filteredRecipients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">No matching recipients found.</td>
                  </tr>
                ) : (
                  filteredRecipients.map((person) => (
                    <tr key={person.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                      <td className="px-4 py-3 text-sm font-medium text-navy-700 dark:text-white">{person.staffId || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                            <MdPerson className="h-4 w-4 text-brand-500" />
                          </div>
                          <span className="text-sm font-medium text-navy-700 dark:text-white">{person.fullName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleBadge(person.role)}`}>{person.role}</span></td>
                      <td className="px-4 py-3">{person.roleType ? <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${roleTypeBadge(person.roleType)}`}>{person.roleType}</span> : <span className="text-xs text-gray-400">—</span>}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{person.department || "—"}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300"><MdPhone className="h-4 w-4 text-gray-400" />{person.contact}</div></td>
                                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                    {passRecords.some((p) => String(p.personId) === String(person.id) && p.status !== 'Revoked') ? (
                                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Has pass</span>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card extra="p-6">
          <div className="mb-5 flex items-center gap-2">
            <MdAdd className="h-5 w-5 text-amber-500" />
            <h3 className="text-base font-semibold text-navy-700 dark:text-white">Grant Pass</h3>
          </div>

          <form onSubmit={handleGrantPass} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Select person</label>
              <select value={selectedPersonId} onChange={(e) => setSelectedPersonId(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white">
                <option value="">Choose recipient…</option>
                {recipients.map((person) => (<option key={person.id} value={person.id}>{person.fullName} ({person.staffId || "No ID"})</option>))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Start date</label>
                <input type="date" value={startDate} min={minDate} max={maxDate} onChange={(e) => handleStartDateChange(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">End date</label>
                <input type="date" value={endDate} min={minDate} max={maxDate} onChange={(e) => handleEndDateChange(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white" />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Reason</label>
              <input type="text" value={passReason} onChange={(e) => setPassReason(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Optional note for HR/shift team" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-800 dark:text-white" />
            </div>

            {formMessage && (<div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/10 dark:text-green-300">{formMessage}</div>)}

            <button type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600"><MdCheckCircle className="h-4 w-4" />Grant Pass</button>
          </form>
        </Card>
      </div>

      <Card extra="p-0 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4 dark:border-navy-700">
            <MdBadge className="h-5 w-5 text-amber-500" />
            <h3 className="text-base font-semibold text-navy-700 dark:text-white">HR Pass Records</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-navy-700">
            <thead className="bg-gray-50 dark:bg-navy-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Person</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Staff ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Dates</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-navy-700 dark:bg-navy-700">
                      {passRecords.length === 0 ? (
                        <tr><td colSpan={5} className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">No pass records yet.</td></tr>
                      ) : (
                        passRecords.map((p) => (
                          <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                            <td className="px-4 py-3 text-sm font-medium text-navy-700 dark:text-white">{p.fullName}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{p.staffId || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{p.startDate} → {p.endDate}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{p.reason}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">{p.status}</span>
                                  <button onClick={() => handleDeletePass(p.id)} className="text-sm text-red-600 hover:underline">Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default HRManager;
